import {
  Box,
  List,
  ListSubheader,
  ListItem,
  Checkbox,
  ListItemText,
  Tooltip,
  ListItemIcon,
  TextField,
} from "@mui/material";
import { IProjectData } from "@types";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { FolderIcon, ArticleIcon } from "../../icons";
import { selectedProjectState } from "../../recoil";
import { getProjectGeneralInfo } from "../../services/projectService";
import { ViewProps } from "../../@types";
import { formatDate } from "../../utils/utils";
import { toast } from "react-toastify";
import { useState } from "react";
import { truncateString } from "common-lib";
import { FileTypeBadge } from "../new/FileTypeBadge";
import { ProjectContextMenu } from "../new/ProjectCard";

export const ListView = ({
  projectsData,
  folderList,
  checkedProjects,
  checkedFolders,
  currentPath,
  setProjectsData,
  setFolderList,
  setCheckedProjects,
  setCheckedFolders,
  setCurrentPath,
  setCurrentFolder,
  setIsCopied,
  onDragStart,
  onDragEnd,
  onDrop,
  setOpenFolderInfo,
}: ViewProps) => {
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useRecoilState(selectedProjectState);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; project: IProjectData } | null>(null);

  const handleFolderToggle = (id: number) => {
    setCheckedFolders(
      checkedFolders.includes(id) ? checkedFolders.filter((fid) => fid !== id) : [...checkedFolders, id],
    );
  };

  const handleProjectToggle = (id: number) => {
    setCheckedProjects(
      checkedProjects.includes(id) ? checkedProjects.filter((pid) => pid !== id) : [...checkedProjects, id],
    );
  };

  // 폴더 더블클릭: 경로 진입
  const handleFolderDoubleClick = (folder: FolderItem) => {
    setCurrentFolder(folder);
    if (folder.id !== -1) {
      setCurrentPath(currentPath + "/" + folder.name);
    }
  };

  // 프로젝트 더블클릭
  const handleProjectDoubleClick = async (event: React.MouseEvent<HTMLElement>, data: IProjectData) => {
    event.preventDefault();
    if (data.step === "분석") {
      const response = await getProjectGeneralInfo(data.id);
      if (response.project.class.length < 1) {
        navigate(`/main/project/${data.id}/analysis/pre_class`);
      } else {
        navigate(`/main/project/${data.id}/analysis`);
      }
    } else {
      navigate(`/main/project/${data.id}`);
    }
    setSelectedProject(data.id);
  };

  const handleFolderCheck = (e: React.MouseEvent, folder: FolderItem) => {
    e.stopPropagation();
    setCheckedFolders((prev: number[]) => {
      if (prev.includes(folder.id)) {
        return prev.filter((id) => id !== folder.id);
      } else {
        setOpenFolderInfo(true);
        return [...prev, folder.id];
      }
    });
  };

  const handleProjectCheck = (e: React.MouseEvent, project: ProjectItem) => {
    e.stopPropagation();
    setCheckedProjects((prev: number[]) => {
      if (prev.includes(project.id)) {
        return prev.filter((id) => id !== project.id);
      } else {
        return [...prev, project.id];
      }
    });
  };

  return (
    <>
      {folderList.map((folder) => (
        <div
          draggable
          key={`folder-${folder.id}`}
          onClick={(e) => handleFolderCheck(e, folder)}
          onDoubleClick={() => handleFolderDoubleClick(folder)}
          onDragOver={(e) => e.preventDefault()}
          onDragStart={(e) => onDragStart(e, folder.name)}
          onDragEnd={onDragEnd}
          onDrop={(e) => onDrop(e, folder.id)}
          style={{
            display: "grid",
            gridTemplateColumns: "40px 40px 220px 110px 170px 140px 140px 1fr",
            alignItems: "center",
            padding: "0 16px",
            height: "44px",
            cursor: "pointer",
            borderBottom: "1px solid var(--dk-border, #f3f4f6)",
            background: checkedFolders.includes(folder.id) ? "#eff6ff" : "#fff",
            borderLeft: checkedFolders.includes(folder.id) ? "2px solid #3b82f6" : "2px solid transparent",
            transition: "all .12s",
          }}
          onMouseEnter={(e) => {
            if (!checkedFolders.includes(folder.id)) (e.currentTarget as HTMLElement).style.background = "#f9fafb";
          }}
          onMouseLeave={(e) => {
            if (!checkedFolders.includes(folder.id)) (e.currentTarget as HTMLElement).style.background = "#fff";
          }}
        >
          <Checkbox
            edge="start"
            onChange={() => handleFolderToggle(folder.id)}
            checked={checkedFolders.includes(folder.id)}
            size="small"
            sx={{ p: "2px", color: "var(--dk-text-3, #d1d5db)", "&.Mui-checked": { color: "#3b82f6" } }}
          />
          <div
            style={{
              width: 26,
              height: 26,
              borderRadius: "6px",
              background: "#fff7ed",
              border: "1px solid #fed7aa",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FolderIcon sx={{ fontSize: 15, color: "#f97316" }} />
          </div>
          <span
            onClick={() => handleFolderDoubleClick(folder)}
            style={{
              fontSize: "13px",
              fontWeight: 500,
              color: "var(--dk-text, #111827)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.textDecoration = "underline")}
            onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.textDecoration = "none")}
          >
            {folder.name}
          </span>
          <span />
          <span />
          <span />
          <span />
          <span style={{ fontSize: "11px", color: "var(--dk-text-3, #d1d5db)" }}>폴더</span>
        </div>
      ))}

      {projectsData.map((project) => (
        <div
          draggable
          key={`project-${project.id}`}
          onClick={(e) => handleProjectCheck(e, project)}
          onDoubleClick={(e) => handleProjectDoubleClick(e, project)}
          onContextMenu={(e) => {
            e.preventDefault();
            setContextMenu({ x: e.clientX, y: e.clientY, project });
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragStart={(e) => onDragStart(e, project.project_name)}
          onDragEnd={onDragEnd}
          style={{
            display: "grid",
            gridTemplateColumns: "40px 40px 220px 110px 170px 140px 140px 1fr",
            alignItems: "center",
            padding: "0 16px",
            height: "44px",
            cursor: "pointer",
            borderBottom: "1px solid var(--dk-border, #f3f4f6)",
            background: checkedProjects.includes(project.id) ? "#eff6ff" : "#fff",
            borderLeft: checkedProjects.includes(project.id) ? "2px solid #3b82f6" : "2px solid transparent",
            transition: "all .12s",
          }}
          onMouseEnter={(e) => {
            if (!checkedProjects.includes(project.id)) (e.currentTarget as HTMLElement).style.background = "#f9fafb";
          }}
          onMouseLeave={(e) => {
            if (!checkedProjects.includes(project.id)) (e.currentTarget as HTMLElement).style.background = "#fff";
          }}
        >
          <Checkbox
            edge="start"
            onChange={() => handleProjectToggle(project.id)}
            checked={checkedProjects.includes(project.id)}
            size="small"
            sx={{ p: "2px", color: "var(--dk-text-3, #d1d5db)", "&.Mui-checked": { color: "#3b82f6" } }}
          />
          <div
            style={{
              width: 26,
              height: 26,
              borderRadius: "6px",
              background: "var(--dk-info-bg, #eff6ff)",
              border: "1px solid var(--dk-info-border, #bfdbfe)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ArticleIcon sx={{ fontSize: 15, color: "#3b82f6" }} />
          </div>

          <Tooltip title={project.project_name} placement="top">
            <span
              onClick={(e) => handleProjectDoubleClick(e, project)}
              style={{
                fontSize: "13px",
                fontWeight: 500,
                color: "var(--dk-text, #111827)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.textDecoration = "underline")}
              onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.textDecoration = "none")}
            >
              {truncateString(project.project_name, 18) || "-"}
            </span>
          </Tooltip>

          <span>
            <FileTypeBadge type={project.step} size="sm" />
          </span>
          <span
            style={{
              fontSize: "12px",
              color: "var(--dk-text-2, #6b7280)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {project.material_name || "-"}
          </span>

          <span style={{ fontSize: "12px", color: "var(--dk-text-2, #374151)", fontWeight: 500 }}>
            {project.labelCount ?? 0}장
          </span>
          <span style={{ fontSize: "12px", color: "var(--dk-text-2, #374151)", fontWeight: 500 }}>
            {project.imageCount ?? 0}장
          </span>
          <span style={{ fontSize: "11px", color: "var(--dk-text-3, #9ca3af)" }}>{formatDate(project.create_at)}</span>
        </div>
      ))}
      {contextMenu && (
        <ProjectContextMenu
          data={contextMenu.project}
          contextMenu={contextMenu}
          onClose={() => setContextMenu(null)}
          projectData={projectsData}
          setProjectsData={setProjectsData}
          setIsCopied={setIsCopied}
        />
      )}
    </>
  );
};
